import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { fmt } from "@/lib/pricing";
import { http } from "@/lib/admin";
import { QuoteEditor } from "./QuoteEditor";
import { Plus, Eye, Pencil, Trash2 } from "lucide-react";

export function QuotesPanel({ draft, onDraftConsumed }) {
  const [quotes, setQuotes] = useState([]);
  const [editor, setEditor] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    http.get("/quotes").then((r) => setQuotes(r.data)).catch(() => toast.error("Nem sikerült betölteni az árajánlatokat"));
  }, []);

  useEffect(() => {
    if (draft) {
      setEditor({ initial: draft });
      onDraftConsumed();
    }
  }, [draft, onDraftConsumed]);

  const remove = async (id) => {
    if (!window.confirm("Biztosan törli ezt az árajánlatot?")) return;
    try {
      await http.delete(`/quotes/${id}`);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
    } catch {
      toast.error("Sikertelen törlés");
    }
  };

  if (editor) {
    return (
      <QuoteEditor
        initial={editor.initial}
        quoteId={editor.quoteId}
        onCancel={() => setEditor(null)}
        onSaved={(q) => navigate(`/admin/arajanlat/${q.id}`)}
      />
    );
  }

  return (
    <div data-testid="quotes-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="eyebrow text-[var(--terra)]">Árajánlatok</p>
          <h2 className="font-serif-d font-semibold text-2xl mt-1">Elkészített ajánlatok</h2>
        </div>
        <button data-testid="quote-new-btn" onClick={() => setEditor({ initial: null })}
          className="btn-terra rounded-full px-6 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Új árajánlat
        </button>
      </div>

      <div className="space-y-3" data-testid="quote-list">
        {quotes.length === 0 && (
          <p className="text-center text-[var(--ink-muted)] py-16 bg-white rounded-2xl border border-[var(--line)]">
            Még nincs elkészített árajánlat. Indítson egyet a leadekből, vagy az „Új árajánlat” gombbal.
          </p>
        )}
        {quotes.map((q) => (
          <div key={q.id} data-testid={`quote-row-${q.id}`}
            className="bg-white border border-[var(--line)] rounded-2xl px-5 py-4 flex flex-wrap items-center gap-3">
            <span className="font-mono-d text-xs bg-[var(--stone-bg2)] border border-[var(--line)] rounded-full px-3 py-1">{q.number}</span>
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{q.customer.name}{q.customer.company ? ` · ${q.customer.company}` : ""}</p>
              <p className="text-xs text-[var(--ink-muted)]">
                {new Date(q.created_at).toLocaleDateString("hu-HU")} · {q.totals.machines} gép · {q.items.length} tétel
              </p>
            </div>
            <span className="ml-auto font-serif-d font-semibold text-[var(--terra)]">{fmt(q.totals.grand_total)}</span>
            <div className="flex items-center gap-1">
              <IconBtn testId={`quote-open-${q.id}`} title="Megnyitás / PDF" onClick={() => navigate(`/admin/arajanlat/${q.id}`)}><Eye className="w-4 h-4" /></IconBtn>
              <IconBtn testId={`quote-edit-${q.id}`} title="Szerkesztés"
                onClick={() => setEditor({ quoteId: q.id, initial: { ...q.customer, lead_id: q.lead_id, items: q.items.map(({ calculation, ...i }) => i), valid_days: q.valid_days, notes: q.notes } })}>
                <Pencil className="w-4 h-4" />
              </IconBtn>
              <IconBtn testId={`quote-delete-${q.id}`} title="Törlés" onClick={() => remove(q.id)} danger><Trash2 className="w-4 h-4" /></IconBtn>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const IconBtn = ({ children, onClick, testId, title, danger }) => (
  <button data-testid={testId} title={title} onClick={onClick}
    className={`p-2 rounded-full transition-colors ${danger ? "text-red-500 hover:bg-red-50" : "text-[var(--ink-soft)] hover:bg-[var(--stone-bg2)] hover:text-[var(--terra)]"}`}>
    {children}
  </button>
);
